import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";

const MAX_ATTEMPTS = 10;
const POLL_MS = 2000;

export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const { lang } = useLang();
  const t = useDict(lang);
  const [state, setState] = useState("checking"); // checking | paid | failed
  const [result, setResult] = useState(null);
  const [downloads, setDownloads] = useState([]);
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) { setState("failed"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const res = await api.checkoutStatus(sessionId);
        if (cancelled) return;
        setResult(res);
        if (res.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          // Fetch any digital downloads
          try {
            const dl = await api.orderDownloads(res.order_id);
            setDownloads(dl.downloads || []);
          } catch (e) {
            if (process.env.NODE_ENV !== "production") console.warn("Downloads not yet available:", e?.message);
          }
          return;
        }
        if (res.status === "expired" || res.payment_status === "failed") { setState("failed"); return; }
        if (attempts.current >= MAX_ATTEMPTS) { setState("failed"); return; }
        setTimeout(poll, POLL_MS);
      } catch {
        if (attempts.current >= MAX_ATTEMPTS) setState("failed");
        else setTimeout(poll, POLL_MS);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-32 text-center">
      {state === "checking" && (
        <>
          <Loader2 size={32} className="mx-auto animate-spin text-[color:var(--ink-2)] mb-6" />
          <h1 data-testid="checkout-checking" className="font-serif-display text-4xl">{lang === 'fr' ? 'Vérification du paiement…' : 'Verifying payment…'}</h1>
        </>
      )}
      {state === "paid" && (
        <>
          <Check size={32} className="mx-auto text-[color:var(--palm)] mb-6" />
          <p className="eyebrow mb-4">{lang === 'fr' ? 'Paiement reçu' : 'Payment received'}</p>
          <h1 data-testid="checkout-paid" className="font-serif-display text-5xl mb-4">{t.checkout.successTitle}</h1>
          <p className="text-[color:var(--ink-2)]">{t.checkout.successSub}</p>
          <p className="font-serif-display text-2xl mt-2">{result?.order_id}</p>
          <p className="text-[color:var(--ink-3)] mt-2 text-sm">{result?.amount?.toFixed(2)} €</p>
          {downloads.length > 0 && (
            <div className="mt-10 border border-[color:rgba(0,16,48,0.15)] p-6 text-left">
              <p className="eyebrow mb-4">{lang === 'fr' ? 'Téléchargements disponibles' : 'Available downloads'}</p>
              {downloads.map((d) => (
                <a
                  key={d.slug}
                  href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`}
                  download
                  data-testid={`download-${d.slug}`}
                  className="flex items-center gap-4 py-3 border-b border-[color:rgba(0,16,48,0.08)] last:border-0 hover:bg-[var(--bg-2)] transition-colors px-3"
                >
                  <img src={d.image} className="w-14 h-14 object-cover bg-[var(--bg-2)]" alt="" />
                  <div className="flex-1">
                    <p className="font-serif-display text-lg">{d.name[lang] || d.name.fr}</p>
                    <p className="text-xs text-[color:var(--ink-3)] uppercase tracking-[0.18em]">PDF</p>
                  </div>
                  <span className="text-xs tracking-[0.22em] uppercase font-medium underline">{lang === 'fr' ? 'Télécharger' : 'Download'} ↓</span>
                </a>
              ))}
            </div>
          )}
          <Link to="/" className="btn-outline mt-10 inline-flex">{t.checkout.backHome}</Link>
        </>
      )}
      {state === "failed" && (
        <>
          <h1 data-testid="checkout-failed" className="font-serif-display text-4xl mb-4">{lang === 'fr' ? 'Paiement non confirmé' : 'Payment not confirmed'}</h1>
          <p className="text-[color:var(--ink-2)] mb-8">{lang === 'fr' ? 'Vérifiez votre boîte mail ou réessayez le paiement.' : 'Check your inbox or try paying again.'}</p>
          <Link to="/checkout" className="btn-navy">{lang === 'fr' ? 'Réessayer' : 'Try again'}</Link>
        </>
      )}
    </div>
  );
}
